/* global module */
'use_strict';

declare var $;
declare var grecaptcha;

import { ICSCartJsonResponse } from '../tv_shipping/ICSCartJsonResponse';
import { IAddress } from '../tv_shipping/IAddress';
import { init_g_recaptcha } from '../tv_third_party_integrations/g_recaptcha';
import { EmailValidation } from '../tv_address_book/email_validation';
import { Notifications } from '../tv_utils/notifications';

/**
 * Class to handle the users javascript.
 */
export class ResellerView {

    resellerAccount: HTMLElement;
    websiteRequest: HTMLElement;
    resaleVerification: HTMLElement;
    profileForm: NodeListOf<HTMLElement>;

    // Email inputs and label from the registration form and billing address form.
    register_email_input: HTMLElement;
    billing_email_input: HTMLElement;
    billing_email_label: HTMLElement;

    // Manipulated objects
    resaleVerificationLabel: HTMLElement;
    resaleSection: HTMLElement;
    resaleCertificate: HTMLElement;
    resaleCertificateLabel: HTMLElement;
    resaleWebsite: HTMLElement;
    resaleWebsiteLabel: HTMLElement;
    resaleExplanation: HTMLElement;
    resaleExplanationLabel: HTMLElement;
    websiteSection: HTMLElement;
    websiteDomainOneLabel: HTMLElement;
    websiteDomainTwoLabel: HTMLElement;
    websiteDomainThreeLabel: HTMLElement;
    websiteLogoLabel: HTMLElement;


    constructor() {
        // Bound objects
        this.resellerAccount = document.getElementById('reseller_account');
        this.resaleVerification = document.getElementById('resale_verification');
        this.websiteRequest = document.getElementById('website_request');
        this.profileForm = document.getElementsByName('profile_form');

        // Bind the email input for registering a new account.
        this.register_email_input = document.getElementById('email');

        // Bind the email input from the billing address form.
        this.billing_email_input = document.querySelector('#js-step-one-profile-fields input[name="user_data[email]"]');

        // When registering for an account, there are duplicate email inputs.
        // One is from the registration form, and the other is from the billing address form.
        // Remove the email input and label from the billing address form.
        if (this.register_email_input && this.billing_email_input) {
            this.billing_email_label = <HTMLElement> this.billing_email_input.previousElementSibling;

            this.billing_email_input.remove();
            this.billing_email_label.remove();
        }


        // Manipulated objects
        this.resaleVerificationLabel = document.getElementById('resale_verification_label');
        this.resaleSection = document.getElementById('reseller_section');
        this.resaleCertificate = document.getElementById('resale_certificate');
        this.resaleCertificateLabel = document.getElementById('resale_certificate_label');
        this.resaleWebsite = document.getElementById('resale_website');
        this.resaleWebsiteLabel = document.getElementById('resale_website_label');
        this.resaleExplanation = document.getElementById('resale_explanation');
        this.resaleExplanationLabel = document.getElementById('resale_explanation_label');
        this.websiteSection = document.getElementById('reseller_website_section');
        this.websiteDomainOneLabel = document.getElementById('website_domain_one_label');
        this.websiteDomainTwoLabel = document.getElementById('website_domain_two_label');
        this.websiteDomainThreeLabel = document.getElementById('website_domain_three_label');
        this.websiteLogoLabel = document.getElementById('website_logo_label')

        // Fields that will exist on the user sign up page but not the reseller account request.
        if (this.resellerAccount !== null) {
            this.resellerAccount.addEventListener('click', this.clickResellerAccount.bind(this));

            // Only execute if the form exists.
            if (this.profileForm.length > 0) {
                this.profileForm[0].setAttribute('enctype', 'multipart/form-data');
            }
        }

        // Check for the select since it's not there when clicking on 'change' on the checkout when signed in.
        if (this.resaleVerification) {
            this.resaleVerification.addEventListener('change', this.changeResaleVerification.bind(this));
        }
    }

    /**
     * Handles change events for updating the required reseller verification fields.
     *
     * @listens change
     * @param {Event} e from change event
     * @returns {void}
     */
    changeResaleVerification(e) {
        this.resaleCertificate.classList.add('hidden');
        this.resaleCertificateLabel.classList.remove('cm-required');
        this.resaleWebsite.classList.add('hidden');
        this.resaleWebsiteLabel.classList.remove('cm-required');
        this.resaleExplanation.classList.add('hidden');
        this.resaleExplanationLabel.classList.remove('cm-required');

        if (e.currentTarget.value === 'certificate') {
            this.resaleCertificate.classList.remove('hidden');
            this.resaleCertificateLabel.classList.add('cm-required');
        } else if (e.currentTarget.value === 'website') {
            this.resaleWebsite.classList.remove('hidden');
            this.resaleWebsiteLabel.classList.add('cm-required');
        } else if (e.currentTarget.value === 'explanation') {
            this.resaleExplanation.classList.remove('hidden');
            this.resaleExplanationLabel.classList.add('cm-required');
        }

    }

    /**
     * Handles button clicks for showing the reseller sections of the sign up.
     *
     * @listens click
     * @param {Event} e from click event
     * @returns {void}
     */
    clickResellerAccount(e) {
        this.resaleSection.classList.add('hidden');
        this.resaleVerificationLabel.classList.remove('cm-required');

        if (e.currentTarget.checked) {
            this.resaleSection.classList.remove('hidden');
            this.resaleVerificationLabel.classList.add('cm-required');
        }

    }

}

/**
 * Validate addresses on the registration page.
 */
export class RegistrationAddressValidator {

    /**
     * @var elements on the page.
     * @var status of each address.
     * @var checkout if this is the checkout.
     */
    elements:any;
    status:any;
    checkout:boolean;

    /**
     * Make the thing.
     *
     * @param {boolean} checkout true if this is the checkout.
     * @returns {RegistrationAddressValidator} ready made.
     */
    constructor(checkout:boolean = false) {
        this.checkout = checkout;

        // Bind to the dom.
        this.elements = {
            user_account_information: {
                password1: document.getElementById('password1'),
                password2: document.getElementById('password2')
            },
            billing_address: {
                firstname: document.querySelector('input[name="user_data[b_firstname]"]'),
                lastname: document.querySelector('input[name="user_data[b_lastname]"]'),
                company: document.querySelector('input[name="user_data[fields][40]"]'),
                email: document.querySelector('input[name="user_data[email]"]'),
                phone: document.querySelector('input[name="user_data[b_phone]"]'),
                address1: document.querySelector('input[name="user_data[b_address]"]'),
                address2: document.querySelector('input[name="user_data[b_address_2]"]'),
                city: document.querySelector('input[name="user_data[b_city]"]'),
                state_ddl: document.querySelector('select[name="user_data[b_state]"]'),
                state: document.querySelector('input[name="user_data[b_state]"]'),
                zipcode: document.querySelector('input[name="user_data[b_zipcode]"]'),
                country: document.querySelector('select[name="user_data[b_country]"]')
            },
            contact_information: {
                firstname: document.querySelector('input[name="user_data[firstname]"]'),
                lastname: document.querySelector('input[name="user_data[lastname]"]'),
                company: document.querySelector('input[name="user_data[company]"]'),
                phone: document.querySelector('input[name="user_data[phone]"]')
            },
            delivery_address: {
                firstname: document.querySelector('input[name="user_data[s_firstname]"]'),
                lastname: document.querySelector('input[name="user_data[s_lastname]"]'),
                company: document.querySelector('input[name="user_data[fields][40]"]'),
                phone: document.querySelector('input[name="user_data[s_phone]"]'),
                address1: document.querySelector('input[name="user_data[s_address]"]'),
                address2: document.querySelector('input[name="user_data[s_address_2]"]'),
                city: document.querySelector('input[name="user_data[s_city]"]'),
                state_ddl: document.querySelector('select[name="user_data[s_state]"]'),
                state: document.querySelector('input[name="user_data[s_state]"]'),
                zipcode: document.querySelector('input[name="user_data[s_zipcode]"]'),
                country: document.querySelector('select[name="user_data[s_country]"]')
            },

            email_is_registered_message: document.getElementById('js-tv-email-is-registered'),
            registration_sections: document.getElementsByClassName('js-tv-registration-section'),
            buttons_container: document.querySelector('.ty-profile-field__buttons')
        };

        this.elements.contact_information.phone.addEventListener(
            'blur', () => this.phone_validation(this.elements.contact_information.phone)
        );

        // Hide and disable the registration fields on load to prevent browser auto-fill from causing a change event.
        this.toggle_registration_inputs(true, true);

        (() => new EmailValidation(
            this.elements.billing_address.email,
            this.handle_check_email_for_registration.bind(this),
            true
        ))();

        // Init the statuses.
        this.status = {
            billing_address: {
                valid: false
            },
            delivery_address: {
                valid: this.checkout
            }
        }

        // Set change events on the inputs so that the user has to revalidate if they adjust something.
        for (const field of Object.keys(this.elements.billing_address)) {
            if (this.elements.billing_address[field]) {
                this.elements.billing_address[field].addEventListener('change', this.changed.bind(this));
            }
        }

        // Shipping fields and the "same as billing" aren't on the checkout page.
        if (!this.checkout) {
            for (const field of Object.keys(this.elements.delivery_address)) {
                if (this.elements.delivery_address[field]) {
                    this.elements.delivery_address[field].addEventListener('change', this.changed.bind(this));
                }
            }

            for (const radio of document.querySelectorAll("input[name='ship_to_another'][type='radio']")) {
                radio.addEventListener('change', this.changed.bind(this));
            }
        }

        // Create a new button to trigger the validation when clicked.
        if (this.elements.submit_button) {
            this.elements.validate_button = document.createElement("button");
            this.elements.validate_button.classList = this.elements.submit_button.classList;
            this.elements.validate_button.innerHTML = 'Validate & Register';
            this.elements.validate_button.setAttribute('type', 'button');
            this.elements.buttons_container.prepend(this.elements.validate_button);

            this.elements.validate_button.addEventListener('click', this.validate_clicked.bind(this));

            // Hide the native submit button.
            this.elements.submit_button.classList.add('hidden');
        }
    }

    /**
     * Returns the address from the fields of the desired type.
     *
     * @param {string} type delivery_address or billing_address
     * @returns {IAddress} created from the values of the fields.
     */
    address(type:string):IAddress {
        const fields = this.elements[type];

        // Handle the state drop down list vs the hidden state field.
        let state = fields.state_ddl.value;
        if (!state) {
            state = fields.state.value;
        }

        return {
            name: fields.firstname.value + fields.lastname.value,
            company: fields.company.value,
            address1: fields.address1.value,
            address2: fields.address2.value,
            city: fields.city.value,
            state: state,
            zipcode: fields.zipcode.value,
            country: fields.country.value,
            phone: fields.phone.value,
            email: '',
            recid: ''
        };
    }

    /**
     * Resets the validation status if a form input is changed.
     * @param {Event} e that triggered the click.
     * @returns {void}
     */
    changed(e:Event):void {
        this.status.delivery_address.valid = this.checkout;
        this.status.billing_address.valid = false;
    }

    /**
     * Toggle attribute fields for registration.
     *
     * @param {boolean} disable true to disable inputs.
     * @param {boolean} hide true to hide the section.
     * @returns {void}
     */
    toggle_registration_inputs(disable: boolean, hide: boolean): void {
        // Disable or enable the user registration fields.
        for (const field of Object.keys(this.elements.user_account_information)) {
            if (this.elements.user_account_information[field]) {
                this.elements.user_account_information[field].disabled = disable;
                this.elements.user_account_information[field].classList.remove('tv-disabled');
                this.elements.user_account_information.password1.focus();
                if (disable) {
                    this.elements.user_account_information[field].classList.add('tv-disabled');
                }
            }
        }

        for (const field of Object.keys(this.elements.billing_address)) {
            // Don't enable/disable the email field as we always want it to be editable.
            // Also want to ignore the state field because we don't want to mess with the disabled field on
            // that hidden text input.
            if (this.elements.billing_address[field] && field !== 'email' && field !== 'state') {
                this.elements.billing_address[field].disabled = disable;
                this.elements.billing_address[field].classList.remove('tv-disabled');
                if (disable) {
                    this.elements.billing_address[field].classList.add('tv-disabled');
                }
            }
        }

        for (const field of Object.keys(this.elements.contact_information)) {
            if (this.elements.contact_information[field]) {
                this.elements.contact_information[field].disabled = disable;
                this.elements.contact_information[field].classList.remove('tv-disabled');
                if (disable) {
                    this.elements.contact_information[field].classList.add('tv-disabled');
                }
            }
        }

        // When disabled, this prevents the form from submitting on 'enter' key.
        if (this.elements.submit_button) {
            this.elements.submit_button.disabled = disable;
        }

        // Control the display of the registration sections.
        for (const section of this.elements.registration_sections) {
            section.style.display = 'block';
            if (hide) {
                section.style.display = 'none';
            }
        }
    }

    /**
     * Handle the response from the check for if the email was registered for a webstore account.
     *
     * @param {string} email The email addressed that was checked.
     * @param {ICSCartJsonResponse} response from the native AJAX.
     */
    handle_check_email_for_registration(email: string, response: ICSCartJsonResponse) {
        // If the email is registered, notify the user by displaying a notification and
        // a message with links to login or reset their password. Disable the fields too
        // so they don't try to finish filling out the form.
        if (response.is_registered) {
            const sign_in_button = document.getElementById('js-login-button');
            const sign_in_email_input = document.querySelector('.ty-login-popup .ty-login__input');
            
            sign_in_button.click();
            sign_in_email_input.value = email;

            // Set the cscart notification.
            Notifications.set_notification(
                'E',
                'Account exists',
                `An account for '${email}' already exists.`
            )

            const login_modal = document.getElementsByClassName('object-container')[0] as HTMLElement;
            const error_message = document.querySelector('.ui-dialog .cm-notification-container-dialog') as HTMLElement;
            if (error_message) {
                error_message.style.position = 'static';
                error_message.style.maxWidth = '340px';
                login_modal.style.height = '';
            }

            // Display the message under the email input notifying the user of the registered email.
            if (this.elements.email_is_registered_message) {
                this.elements.email_is_registered_message.style.display = 'block';
            }

            // Disable the inputs to make sure the user doesn't try to complete the rest of the form.
            this.toggle_registration_inputs(true, true);

            return;
        }

        // Hide the message and enable the fields if the email is good to register.
        if (this.elements.email_is_registered_message) {
            this.elements.email_is_registered_message.style.display = 'none';
        }

        // Enable the inputs if email is good to be registered.
        this.toggle_registration_inputs(false, false);
    }

    /**
     * Checks validity of the entered phone number.
     * 
     * @param {HTMLFormElement} phone_input is the phone field for this form.
     */
    phone_validation(phone_input) {
        const phone_validation = /^\(?([0-9]{3})\)?[- ]?([0-9]{3})[- ]?([0-9]{4})$/;
        const phone_number = phone_input.value;
        
        phone_input.setCustomValidity('');
        if (!phone_number.match(phone_validation)) {
            phone_input.setCustomValidity(
                "The phone number in the Phone field is invalid. Please enter your phone number in this format: 555-555-5555 or 5555555555."
            );
        }
        phone_input.reportValidity();
    }

    /**
     * Updates the html inputs with the passed address.
     *
     * @param {string} type delivery_address or billing_address.
     * @param {IAddress} address to set.
     * @returns {void}
     */
    set_address(type:string, address:IAddress):void {
        const fields = this.elements[type];

        fields.address1.value = address.address1 ?? address.street;
        fields.address2.value = address.address2 ?? '';
        fields.city.value = address.city;
        fields.state.value = address.state;
        fields.state_ddl.value = address.state;
        fields.zipcode.value = address.zip ?? address.zipcode;
    }

    /**
     * If the addresses are valid display the submit button.
     *
     * @returns {void}
     */
    submit():void {
        if (this.status.billing_address.valid === false || this.status.delivery_address.valid === false) {
            return;
        }

        // We have to mimic a mouse click event if the parent form is using cm-ajax.
        if (this.elements.submit_button.form.classList.contains('cm-ajax')) {
            const evt = new MouseEvent('click', { bubbles: true, cancelable: false });
            this.elements.submit_button.dispatchEvent(evt);
        } else {
            this.elements.submit_button.click();
        }

    }

    /**
     * We have to intercept the button click, since it's the only visible button on the form it will try to submit.
     * So prevent default, then call the validation logic.
     *
     * @param {Event} e that triggered the click.
     * @returns {void}
     */
    validate_clicked(e:Event):void {
        e.preventDefault();
        e.stopPropagation();
        this.validate();
    }

    /**
     * Reaches out to the shipping service and validates the addresses.
     *
     * @returns {void}
     */
    validate(): void {
        $.ceAjax('request', 'index.php?dispatch=tv_shipping.validate_address', {
            method: 'POST',
            caching: true,
            data: {
                address: this.address('billing_address')
            },
            callback: this.validate_callback.bind(this, 'billing_address')
        });

        // If delivery and billing are NOT The same.
        if (this.checkout === false &&
            (<HTMLInputElement> document.querySelector("input[name='ship_to_another']:checked")).value === '1'
        ) {
            $.ceAjax('request', 'index.php?dispatch=tv_shipping.validate_address', {
                method: 'POST',
                caching: true,
                data: {
                    address: this.address('delivery_address')
                },
                callback: this.validate_callback.bind(this, 'delivery_address')
            });
        } else {
            this.status.delivery_address.valid = true;
            this.status.delivery_address.adjusted = false;
        }
    }

    /**
     * Handles a validation response to notify the user if there
     * address failed or was adjusted, and then update the status.
     * Attempt to display the submit button if possible.
     *
     * @param {string} address_type billing_address or delivery_address.
     * @param {ICSCartJsonResponse} response from the native AJAX.
     * @returns {void}
     */
    validate_callback(address_type:string, response: ICSCartJsonResponse): void {
        const type = address_type.replace('_', ' ');
        const data: {status: string, address: IAddress, adjusted: boolean} = response.json;

        // Send the request through unless it is returned invalid by Ship Engine.
        if (data.status === 'valid') {
            this.set_address(address_type, data.address);
            this.status[address_type].valid = true;
            this.submit();

        } else if (data.status === 'invalid') {
            Notifications.set_notification('E', 'Error', `Unable to verify your ${type} with shipping provider`, false);
            this.status[address_type].valid = false;
        }
    }

}

/**
 * Class used for Guest Registration Minimum Template.
 */
export class MiniCustomerRegistration {

    /**
     * @var {array} reg_inputs are the Inputs needed for Guest Registration.
     * @var {[key: string]: HTMLInputElement} inputs are the named inputs for Guest Registration.
     * @var {HTMLElement} email_is_registerd is the message presented to the Customer if the email entered is already registered.
     * @var {HTMLButtonElement} submit_btn is the registration button sent in from the Script Class initializing this one.
     * @var {HTMLElement} login_link is to switch from Mini Reg to Login for emails that have an account.
     * @var {HTMLButtonElement} cancel_login is to return to Mini Reg and clear input fields.
     * @var {HTMLButtonElement} sign_in_button will allow Customers to sign in with the email that has an account.
     * @var {Function} sign_in_callback is what will occur when the Sign In Ajax returns.
     * @var {string} extension_id is the ID for each unique MiniReg on a page.
     * @var {boolean} show_login flag to determine if the Login Buttons should be visible.
     * @var {HTMLElement} g_recaptcha is the Specific ID for the Google reCaptcha.
     */
    reg_inputs = ['firstname', 'lastname', 'email', 'phone', 'passw1', 'passw2'];
    inputs: { [key: string]: HTMLInputElement } = {};
    email_is_registerd: HTMLElement;
    submit_btn: HTMLButtonElement;
    login_link: HTMLElement;
    cancel_login: HTMLButtonElement;
    sign_in_button: HTMLButtonElement;
    sign_in_callback: Function;
    signed_in: boolean = false;
    extension_id: string;
    show_login: boolean = false;
    g_recaptcha: HTMLElement;

    /**
     * Constructor called when this Class is initialized.
     *
     * @param {HTMLButtonElement} submit_btn is the registration button sent in from the calling Class.
     * @param {Function} sign_in_callback is the callback from where this class is being called from.
     * @param {string} extension_id is the ID for each unique MiniReg on a page.
     */
    constructor(
        submit_btn: HTMLButtonElement = null,
        sign_in_callback: Function = null,
        extension_id: string = ''
    ) {
        this.submit_btn = submit_btn;
        // Bind the Submit to Register Button if one is provided.
        if (this.submit_btn) {
            this.submit_btn.addEventListener('click', this.register.bind(this));
        }

        this.sign_in_callback = sign_in_callback;
        this.extension_id = extension_id ? `-${extension_id}` : '';

        this.inputs = {
            firstname: <HTMLInputElement>document.getElementsByClassName(`js-guest-firstname${this.extension_id}`)[0],
            lastname: <HTMLInputElement>document.getElementsByClassName(`js-guest-lastname${this.extension_id}`)[0],
            phone: <HTMLInputElement>document.getElementsByClassName(`js-guest-phone${this.extension_id}`)[0],
            email: <HTMLInputElement>document.getElementsByClassName(`js-guest-email${this.extension_id}`)[0],
            passw1: <HTMLInputElement>document.getElementsByClassName(`js-guest-passw1${this.extension_id}`)[0],
            passw2: <HTMLInputElement>document.getElementsByClassName(`js-guest-passw2${this.extension_id}`)[0]
        };

        for (const [key, field] of Object.entries(this.inputs)) {
            if (key === 'passw1' && field) {
                // If this is the first password field.
                field.addEventListener('keydown', e => {
                    if (e.keyCode === 13) {
                        e.preventDefault();
                        // Then we want it to Sign In if that is what we're trying to do.
                        if (this.show_login) {
                            this.sign_in();
                        } else {
                            this.inputs[key].blur();
                        }
                        return false;
                    }
                });
            } else if (field) {
                // We don't want the enter button on these fields to submit the Design Service form.
                field.addEventListener('keydown', e => {
                    if (e.keyCode === 13) {
                        e.preventDefault();
                        this.inputs[key].blur();
                        return false;
                    }
                });
            }
        }

        this.login_link = <HTMLElement>document.getElementById(`js-mini-reg-login${this.extension_id}`);
        if (this.login_link) {
            this.login_link.addEventListener('click', this.switch_login.bind(this));
        }

        this.cancel_login = <HTMLButtonElement>document.getElementsByClassName(`js-mini-reg-cancel-login${this.extension_id}`)[0];
        if (this.cancel_login) {
            this.cancel_login.addEventListener('click', this.switch_login.bind(this));
        }

        this.sign_in_button = <HTMLButtonElement>document.getElementsByClassName(`js-mini-reg-sign-in${this.extension_id}`)[0];
        if (this.sign_in_button) {
            this.sign_in_button.addEventListener('click', this.sign_in.bind(this));
        }

        this.g_recaptcha = document.querySelector(`.js-recaptcha${this.extension_id} .g-recaptcha`);
        // Initialize the Google Recaptcha for our CSS class.
        init_g_recaptcha(this.g_recaptcha);

        (() => new EmailValidation(this.inputs.email, this.handle_check_email_for_registration.bind(this), true))();
    }

    /**
     * Method to allow Customers to sign in. The redirect is determined by
     * the current locations URL containing the variable 'return_url'.
     *
     * @return {void}
     */
    sign_in() {
        // Close Notification.
        const close_notification = <HTMLElement> document.querySelector('.cm-notification-close');
        if (close_notification) {
            close_notification.click();
        }

        $.ceAjax('request',
            'index.php?dispatch=auth.login', {
                method: 'POST',
                caching: false,
                message: 'Signing In',
                data: {
                    user_login: this.inputs.email.value,
                    password: this.inputs.passw1.value
                },
                callback: (response) => {
                    // Notification type (E - error, W - warning, N - notice, O - order error on checkout, I - information).
                    for (const [key, notification] of Object.entries(response.notifications)) {
                        // E indicates an Error such as Sign In is Incorrect.
                        if (notification['type'] !== 'E' && this.sign_in_callback) {
                            this.signed_in = true;
                            this.sign_in_callback();
                        }
                    }
                }
            }
        );
    }

    /**
     * Method used to hide the registration inputs and show the login inputs or vice versa.
     * This also closes notifications such as the one shown for
     * when emails are found to be registered to an account.
     *
     * @return {void}
     */
    private switch_login() : void {
        // Get the right Display type.
        const block_none = this.show_login ? 'block' : 'none';

        // Hide or Show inputs related only to Registration.
        this.inputs.firstname.style.display = block_none;
        (<HTMLLabelElement> this.inputs.firstname.previousElementSibling).style.display = block_none;
        this.inputs.lastname.style.display = block_none;
        (<HTMLLabelElement> this.inputs.lastname.previousElementSibling).style.display = block_none;
        this.inputs.phone.style.display = block_none;
        (<HTMLLabelElement> this.inputs.phone.previousElementSibling).style.display = block_none;
        this.inputs.passw2.style.display = block_none;
        (<HTMLLabelElement> this.inputs.passw2.previousElementSibling).style.display = block_none;

        // Do the opposite to the Sign In Buttons.
        (<HTMLInputElement>document.getElementsByClassName(
            `tv-mini-reg-login-buttons${this.extension_id}`
        )[0]).style.display = this.show_login ? 'none' : 'block';

        // Clear the email input if switching back to Registration mode.
        if (this.show_login) {
            this.inputs.email.value = '';
            this.inputs.email.focus();
        }

        // Close Notification.
        const close_notification = <HTMLElement> document.querySelector('.cm-notification-close');
        if (close_notification) {
            close_notification.click();
        }

        // Enable inputs if they where disabled.
        this.toggle_registration_inputs(false);
        // Switch the flag.
        this.show_login = !this.show_login;
    }

    /**
     * Handle the response from the check for if the email was registered for a webstore account.
     *
     * @param {string} email The email addressed that was checked.
     * @param {ICSCartJsonResponse} response from the native AJAX.
     * @return {void}
     */
    private handle_check_email_for_registration(email: string, response: ICSCartJsonResponse) : void {
        // If the email is registered, notify the user by displaying a notification and
        // a message with links to login or reset their password. Disable the fields too
        // so they don't try to finish filling out the form.
        if (response.is_registered) {
            // Set the cscart notification.
            Notifications.set_notification(
                'E',
                'Account exists',
                `The provided email: ${email} is already associated with an account in our system.`
            );

            this.switch_login();
            this.inputs.passw1.focus();
            return;
        }

        // Enable the inputs if email is good to be registered.
        this.toggle_registration_inputs(false);
    }

    /**
     * Method to disable inputs when the entered email is a registered one.
     *
     * @param {boolean} disable determines wether we are disabling or enabling the intputs.
     * @param {string} validate is the input that needs validated while the other intputs are disabled.
     * @return {void}
     */
    private toggle_registration_inputs(disable: boolean, validate: string = '') : void {
        for (const input of this.reg_inputs) {
            if (input !== validate) {
                (this.inputs[input]).disabled = disable;
                this.inputs[input].classList.remove('tv-disabled');
                if (disable) {
                    this.inputs[input].classList.add('tv-disabled');
                }
            }
        }

        if (this.submit_btn) {
            this.submit_btn.style.display = disable ? 'none' : 'block';
        }
    }

    /**
     * Method used to validate the Mini Registration Form inputs.
     *
     * @return {boolean}
     */
    validate() : boolean {
        // If the Customer has already Signed in, then
        // this form is considered valid.
        if (this.signed_in) {
            return true;
        }

        // Validate Inputs.
        for (const input of this.reg_inputs) {
            if (!this.inputs[input].validity.valid) {
                this.inputs[input].focus();
                this.inputs[input].reportValidity();
                return false;
            }
        }

        // Check passwords.
        this.inputs.passw2.setCustomValidity('');
        if (this.inputs.passw2.value !== this.inputs.passw1.value) {
            this.inputs.passw2.setCustomValidity('Passwords do not match');
            this.inputs.passw2.focus();
            this.inputs.passw1.reportValidity();
            return false;
        }

        // If there's no ID or no children.
        if (!this.g_recaptcha.id || this.g_recaptcha.childElementCount === 0) {
            // Set the cscart notification.
            Notifications.set_notification('W', 'reCaptcha Validation Failed', `Google reCaptcha needs re-initialized.`);
            // Google reCaptcha needs re-initialized.
            init_g_recaptcha(this.g_recaptcha);
            return false;
        }

        // Validate the Google reCaptcha by grabbing our Custom CSS Class for its response.
        const recaptcha_response = <HTMLInputElement>this.g_recaptcha.querySelector('.g-recaptcha-response');
        if (grecaptcha.getResponse(this.g_recaptcha.id).length === 0) {
            recaptcha_response.setCustomValidity('Recaptcha requires verification.');
            recaptcha_response.setAttribute('required', 'required');
            recaptcha_response.reportValidity();
            return false;
        } else {
            recaptcha_response.removeAttribute('required');
            recaptcha_response.setCustomValidity('');
        }

        return true;
    }

    /**
     * Method to Register Guest Contact Information.
     *
     * @param {string} message is presented to the customer during the Ajax request.
     * @param {boolean} redirect is a flag to tell the PHP controller to redirect.
     * @param {Function} callback when registration is complete.
     * @return {void}
     */
    register(message: string = 'Registering Customer', redirect: boolean = true, callback: Function = null) {
        // If the Customer has already Signed in, then
        // we should just call the callback immediately.
        if (this.signed_in) {
            callback();
            return;
        }

        $.ceAjax('request',
            'index.php?dispatch=profiles.update', {
                method: 'POST',
                caching: false,
                message: message,
                data: {
                    selected_section: 'general',
                    default_cc: '',
                    profile_id: (<HTMLInputElement> document.getElementById('js-profile-id')).value,
                    user_data: {
                        email: this.inputs.email.value,
                        password1: this.inputs.passw1.value,
                        password2: this.inputs.passw2.value,
                        firstname: this.inputs.firstname.value,
                        lastname: this.inputs.lastname.value,
                        phone: this.inputs.phone.value,
                    },
                    'g-recaptcha-response': grecaptcha.getResponse(this.g_recaptcha ? this.g_recaptcha.id : ''),
                    redirect: redirect
                },
                callback: () => {
                    if (callback) {
                        callback();
                    }
                }
            }
        );
    }
}

/**
 * Class to help Save the Password for a Guest after Checkout (Complete).
 */
class SavePassword {

    /**
     * @var {HTMLFormElement} save_form is the Save Password form in Checkout Complete.
     * @var {HTMLButtonElement} save_btn is the anchor button to save the Password.
     * @var {HTMLCollectionOf<HTMLInputElement>} elements are the collection of Form Inputs.
     */
    save_form: HTMLFormElement;
    save_btn: HTMLButtonElement;
    elements: HTMLCollectionOf<HTMLInputElement>;

    /**
     * Receives the Save Password Form, retrieves all inputs in the Form, and
     * finds and binds the Save Button.
     *
     * @param {HTMLFormElement} save_form is the Save Password form in Checkout Complete.
     * @return {SavePassword}
     */
    constructor(save_form: HTMLFormElement) {
        this.save_form = save_form;
        this.elements = <HTMLCollectionOf<HTMLInputElement>> this.save_form.elements;

        this.save_btn = this.save_form.querySelector('.buttons-container a');
        this.save_btn.addEventListener('click', this.save_password.bind(this));
    }

    /**
     * Method bound to Save Button that validates inputs, sends
     * an AJAX request, and handles the response.
     *
     * @return {void}
     */
    save_password() : void {
        const user_id = this.elements.namedItem('profile_id').value;
        if (!user_id) {
            this.save_form.style.display = 'none';

            // Set the cscart notification.
            Notifications.set_notification(
                'E',
                'Form Error',
                `There has been a form error. Please check your email for instructions on how to save a password.`
            );
        }

        const pass1 = this.validate_user_input('user_data[password1]');
        if (!pass1) {
            return;
        }

        const pass2 = this.validate_user_input('user_data[password2]');
        if (!pass2) {
            return;
        }

        if (pass1 !== pass2) {
            this.elements.namedItem('user_data[password2]').value = '';
            this.elements.namedItem('user_data[password1]').value = '';
            this.elements.namedItem('user_data[password1]').focus();

            // Set the cscart notification.
            Notifications.set_notification(
                'W',
                'Passwords do not match',
                `The passwords do not match, please try again.`
            );
        } else {
            $.ceAjax('request', 'index.php?dispatch=profiles.update', {
                method: 'POST',
                caching: false,
                data: {
                    profile_id: user_id,
                    user_data: {
                        password1: pass1,
                        password2: pass2
                    },
                    relogin_user: true // Re-login user to avoid issues
                },
                message: 'Saving Password',
                callback: response => {
                    if (response && response.notifications) {
                        for (const key of Object.keys(response.notifications)) {
                            const notification = response.notifications[key];
                            // The notification type will be 'N' if successful as per fn.users.php line 1815.
                            // The other variables sent to the notification are language variables.
                            if (notification.message && notification.type === 'N') {
                                this.save_form.innerHTML = 'The password has been saved successfully.';
                                this.save_form.parentElement.style.setProperty(
                                    'min-height',
                                    'max-content',
                                    'important'
                                );
                            } else {
                                this.save_form.style.display = 'none';

                                // Set the cscart notification.
                                Notifications.set_notification(
                                    'E',
                                    'Save Password Error',
                                    `There has been an error. ` +
                                    `Please check your email for instructions on how to save a password.`
                                );
                            }
                        }
                    } else {
                        this.save_form.style.display = 'none';

                        // Set the cscart notification.
                        Notifications.set_notification(
                            'E',
                            'Save Password Error',
                            `There has been an error. ` +
                            `Please check your email for instructions on how to save a password.`
                        );
                    }
                }
            });
        }
    }

    /**
     * Helper method to handle finding an Input Element, retrieving its
     * value, and validation.
     *
     * @param {string} name of the Input Element we want, such as 'user_data[password1]'.
     * @return {string} The trimmed and validated value of the Input.
     */
    validate_user_input(name: string) : string {
        const input = this.elements.namedItem(`${name}`);
        const value = input.value.trim();
        if (!value) {
            input.focus();
            input.reportValidity();
            return;
        }
        return value;
    }
}

/**
 * Catch Reseller Sign Up fields and initialize the Reseller Class.
 *
 * @returns {void}
 */
export function init() {
    if (document.getElementById('reseller_account') ||
        document.getElementById('resale_verification') ||
        document.getElementById('website_request')
    ) {
        (() => new ResellerView())();

        if (document.getElementsByName('profile_form')) {
            (() => new RegistrationAddressValidator())();
        }

    }

    // Catch the Save Password form at Checkout Complete.
    const save_password = document.getElementById('tv-guest-save-password');
    if (save_password) {
        (() => new SavePassword(<HTMLFormElement> save_password))();
    }
}
